"use client";

// ===== 출생지 검색 — 전세계 주소/지명 지오코딩 (사주·점성·베딕·수요 공용) =====
// /api/geocode(Nominatim 프록시)로 검색, 자주 찾는 한국 도시는 퀵칩으로 우선 노출.
import { useEffect, useRef, useState } from "react";
import { CITIES } from "./cities";

export interface LocationValue {
  lat: number;
  lon: number;
  label: string;
}

const field: React.CSSProperties = {
  padding: "13px 14px", borderRadius: 13, background: "var(--card)",
  border: "1px solid var(--line-strong)", fontSize: 15, color: "var(--ink)",
  width: "100%", boxSizing: "border-box", font: "inherit",
};

export function LocationSearch({ value, onChange }: { value: LocationValue; onChange: (v: LocationValue) => void }) {
  const [query, setQuery] = useState(value.label);
  const [results, setResults] = useState<LocationValue[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 외부에서 값이 바뀌면(프로필 불러오기 등) 입력창 텍스트도 동기화
  useEffect(() => setQuery(value.label), [value.label]);

  function search(q: string) {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 2) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await fetch(`/api/geocode?q=${encodeURIComponent(q)}`);
        const d = await res.json().catch(() => ({ results: [] }));
        setResults(Array.isArray(d.results) ? d.results : []);
      } catch {
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  }

  function pick(v: LocationValue) {
    onChange(v);
    setQuery(v.label);
    setResults([]);
    setOpen(false);
  }

  return (
    <div style={{ position: "relative" }}>
      <input
        style={field}
        value={query}
        placeholder="태어난 곳 검색 (예: 서울 강남구, Tokyo, New York)"
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQuery(e.target.value); setOpen(true); search(e.target.value); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && (
        <div style={{ position: "absolute", zIndex: 20, top: "calc(100% + 6px)", left: 0, right: 0, borderRadius: 13, background: "var(--card)", border: "1px solid var(--line-strong)", boxShadow: "0 12px 28px rgba(0,0,0,0.18)", overflow: "hidden" }}>
          {query.trim().length < 2 && (
            <div style={{ padding: "10px 12px" }}>
              <div style={{ fontSize: 11, color: "var(--ink-faint)", marginBottom: 6 }}>자주 찾는 도시</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {CITIES.map((c) => (
                  <button key={c.name} onMouseDown={(e) => e.preventDefault()} onClick={() => pick({ lat: c.lat, lon: c.lon, label: c.name })}
                    style={{ font: "inherit", cursor: "pointer", fontSize: 12.5, padding: "6px 11px", borderRadius: 99, background: "var(--chip)", border: "1px solid var(--chip-line)", color: "var(--ink-dim)" }}>
                    {c.name}
                  </button>
                ))}
              </div>
            </div>
          )}
          {searching && <div style={{ padding: "10px 12px", fontSize: 12.5, color: "var(--ink-faint)" }}>검색 중…</div>}
          {!searching && results.length > 0 && (
            <div>
              {results.map((r, i) => (
                <button key={i} onMouseDown={(e) => e.preventDefault()} onClick={() => pick(r)}
                  style={{ display: "block", width: "100%", textAlign: "left", font: "inherit", cursor: "pointer", padding: "10px 12px", fontSize: 12.5, color: "var(--ink)", background: "transparent", border: "none", borderTop: "1px solid var(--line)" }}>
                  {r.label}
                </button>
              ))}
            </div>
          )}
          {!searching && query.trim().length >= 2 && results.length === 0 && (
            <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--ink-faint)" }}>검색 결과가 없어요. 다른 표기로 시도해 보세요.</div>
          )}
        </div>
      )}
    </div>
  );
}
